import { Lecture, LectureStatus, } from "../../types/lecture";
import { getWeek } from "../../utils/date-helper";
import { convertDatesToFirestore, replaceUndefinedWithNull, } from "../../utils/firebase-helper";
import { LectureServiceInterface } from "./LectureServiceInterface";
export class LectureServiceFirestore extends LectureServiceInterface {
    static LECTURES_COLLECTION = "lectures";
    firestore;
    constructor(firestore) {
        super();
        this.firestore = firestore;
    }
    toLecture(doc) {
        const data = doc.data();
        // Handle potential undefined date fields
        if (!data.lectureDate || !data.lectureEndDate) {
            throw new Error(`Lecture document ${doc.id} is missing required date fields`);
        }
        const lectureData = {
            // SAC-synced
            cdid: data.cdid ?? doc.id,
            classroomId: data.classroomId,
            // Convert to strings if stored as numbers (backwards compatibility)
            year: data.year ? String(data.year) : undefined,
            term: data.term ? String(data.term) : undefined,
            teacherId: data.teacherId,
            firstName: data.firstName,
            lastName: data.lastName,
            lectureDate: data.lectureDate.toDate(),
            lectureEndDate: data.lectureEndDate.toDate(),
            topicsCovered: data.topicsCovered ?? null,
            deletedInSAC: data.deletedInSAC ?? false,
            // App-editable, sourced from SAC API
            schoolName: data.schoolName,
            groupName: data.groupName,
            // App-managed
            title: data.title,
            weekNumber: data.weekNumber,
            status: data.status,
            topicIds: data.topicIds ?? [],
            tournamentId: data.tournamentId,
            students: data.students,
            submittedRoll: data.submittedRoll,
            autosavedRoll: data.autosavedRoll,
            leaderboard: data.leaderboard,
        };
        const lecture = new Lecture(lectureData);
        if (data.warmupGames) {
            lecture.warmupGames = data.warmupGames.map((game) => ({
                ...game,
                startTime: game.startTime.toDate(),
                finishTime: game.finishTime?.toDate() || null,
                createdAt: game.createdAt.toDate(),
                updatedAt: game.updatedAt.toDate(),
            }));
        }
        if (data.activities) {
            lecture.activities = data.activities.map((activity) => ({
                ...activity,
                startTime: activity.startTime.toDate(),
                endTime: activity.endTime?.toDate() || null,
                createdAt: activity.createdAt.toDate(),
                updatedAt: activity.updatedAt.toDate(),
            }));
        }
        return lecture;
    }
    async getAll() {
        const snapshot = await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .get();
        return snapshot.docs
            .map((doc) => this.toLecture(doc))
            .filter((l) => !l.deletedInSAC)
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByDateRange(startDate, endDate) {
        const snapshot = await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .where("lectureDate", ">=", this.firestore.timestamp.fromDate(startDate))
            .where("lectureDate", "<=", this.firestore.timestamp.fromDate(endDate))
            .get();
        return snapshot.docs
            .map((doc) => this.toLecture(doc))
            .filter((l) => !l.deletedInSAC)
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByTeacher(teacherId, startDate, endDate) {
        // Requires the composite index (teacherId ASC, lectureDate ASC), managed
        // in the Firebase console. Filtering server-side avoids downloading every
        // coach's lectures in the date range just to keep one teacher's.
        const snapshot = await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .where("teacherId", "==", teacherId)
            .where("lectureDate", ">=", this.firestore.timestamp.fromDate(startDate))
            .where("lectureDate", "<=", this.firestore.timestamp.fromDate(endDate))
            .get();
        return snapshot.docs
            .map((doc) => this.toLecture(doc))
            .filter((l) => !l.deletedInSAC)
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByClassId(classroomId) {
        const snapshot = await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .where("classroomId", "==", classroomId)
            .get();
        return snapshot.docs
            .map((doc) => this.toLecture(doc))
            .filter((l) => !l.deletedInSAC)
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByClassIds(classroomIds) {
        if (classroomIds.length === 0)
            return [];
        const uniqueIds = [...new Set(classroomIds)];
        // Firestore "in" queries accept at most 10 values, so query in chunks.
        const chunks = [];
        for (let i = 0; i < uniqueIds.length; i += 10) {
            chunks.push(uniqueIds.slice(i, i + 10));
        }
        const snapshots = await Promise.all(chunks.map((chunk) => this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .where("classroomId", "in", chunk)
            .get()));
        return snapshots
            .flatMap((snapshot) => snapshot.docs.map((doc) => this.toLecture(doc)))
            .filter((l) => !l.deletedInSAC)
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByTerm(year, term, includeDeletedInSAC = false) {
        // year/term may be stored as numbers (from syncFromSAC) or strings
        // (after updateLecture round-trips through toLecture). Query both.
        const col = this.firestore.collection(LectureServiceFirestore.LECTURES_COLLECTION);
        const [numSnap, strSnap] = await Promise.all([
            col.where("year", "==", year).where("term", "==", term).get(),
            col
                .where("year", "==", String(year))
                .where("term", "==", String(term))
                .get(),
        ]);
        const seen = new Set();
        const lectures = [];
        for (const doc of [...numSnap.docs, ...strSnap.docs]) {
            if (seen.has(doc.id))
                continue;
            seen.add(doc.id);
            const lecture = this.toLecture(doc);
            if (includeDeletedInSAC || !lecture.deletedInSAC) {
                lectures.push(lecture);
            }
        }
        return lectures.sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getLecture(lectureId) {
        const doc = await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .get();
        if (!doc.exists) {
            throw new Error(`Lecture with id ${lectureId} not found`);
        }
        return this.toLecture(doc);
    }
    /** @deprecated All lectures are created via syncFromSAC. This method is no longer used. */
    async createLecture(lectureData) {
        const lecture = new Lecture(lectureData);
        const lectureForFirestore = {
            classroomId: lecture.classroomId,
            year: lecture.year || null,
            term: lecture.term || null,
            schoolName: lecture.schoolName || null,
            groupName: lecture.groupName || null,
            topicIds: lecture.topicIds,
            topicsCovered: lecture.topicsCovered || null,
            weekNumber: lecture.weekNumber,
            lectureDate: this.firestore.timestamp.fromDate(lecture.lectureDate),
            lectureEndDate: this.firestore.timestamp.fromDate(lecture.lectureEndDate), // TODO: recheck this when create lecture
            status: lecture.status,
            teacherId: lecture.teacherId,
            firstName: lecture.firstName,
            lastName: lecture.lastName,
            students: lecture.students || [],
            submittedRoll: lecture.submittedRoll || null,
            autosavedRoll: lecture.autosavedRoll || null,
            createdAt: this.firestore.fieldValue.serverTimestamp(),
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        };
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lecture.id)
            .set(lectureForFirestore);
        return lecture;
    }
    async startLecture(lectureId, teacherId) {
        if (!teacherId) {
            throw new Error("teacherId is required to start a lecture");
        }
        const lecture = await this.getLecture(lectureId);
        lecture.start(teacherId);
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .update({
            status: lecture.status,
            teacherId: lecture.teacherId,
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        });
        return lecture;
    }
    async completeLecture(lectureId) {
        const lecture = await this.getLecture(lectureId);
        lecture.complete();
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .update({
            status: lecture.status,
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        });
        return lecture;
    }
    async updateLecture(lectureId, lectureData) {
        const lecture = await this.getLecture(lectureId);
        lecture.update(lectureData);
        const updateData = {
            topicIds: lecture.topicIds,
            topicsCovered: lecture.topicsCovered || null,
            weekNumber: lecture.weekNumber,
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        };
        if (lectureData.lectureDate) {
            updateData.lectureDate = this.firestore.timestamp.fromDate(lecture.lectureDate);
        }
        // Update year, term, schoolName, groupName if provided
        if (lectureData.year !== undefined) {
            updateData.year = lectureData.year;
        }
        if (lectureData.term !== undefined) {
            updateData.term = lectureData.term;
        }
        if (lectureData.schoolName !== undefined) {
            updateData.schoolName = lectureData.schoolName;
        }
        if (lectureData.groupName !== undefined) {
            updateData.groupName = lectureData.groupName;
        }
        if (lectureData.homeworkAssigned !== undefined) {
            updateData.homeworkAssigned = lectureData.homeworkAssigned;
        }
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .update(updateData);
        return lecture;
    }
    async setTournamentId(lectureId, tournamentId) {
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .update({
            tournamentId,
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        });
    }
    async addWarmupGame(lectureId, gameData) {
        const lectureRef = this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId);
        return this.firestore.runTransaction(async (transaction) => {
            const lectureDoc = await transaction.get(lectureRef);
            if (!lectureDoc.exists) {
                throw new Error(`Lecture ${lectureId} not found`);
            }
            const lecture = this.toLecture(lectureDoc);
            const warmupGame = lecture.addWarmupGame(gameData);
            // Convert dates to Firestore timestamps for storage
            const firestoreWarmupGames = lecture.warmupGames.map((g) => convertDatesToFirestore(this.firestore, g));
            transaction.update(lectureRef, {
                warmupGames: firestoreWarmupGames,
                updatedAt: this.firestore.fieldValue.serverTimestamp(),
            });
            return warmupGame;
        });
    }
    async updateWarmupGameResult(lectureId, gameId, whiteResult, blackResult) {
        const lectureRef = this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId);
        return this.firestore.runTransaction(async (transaction) => {
            const lectureDoc = await transaction.get(lectureRef);
            if (!lectureDoc.exists) {
                throw new Error(`Lecture ${lectureId} not found`);
            }
            const lecture = this.toLecture(lectureDoc);
            lecture.updateWarmupGameResult(gameId, whiteResult, blackResult);
            const updatedGame = lecture.warmupGames.find((g) => g.id === gameId);
            if (!updatedGame) {
                throw new Error(`Warm-up game ${gameId} not found`);
            }
            // Convert dates to Firestore timestamps for storage
            const firestoreWarmupGames = lecture.warmupGames.map((g) => convertDatesToFirestore(this.firestore, g));
            transaction.update(lectureRef, {
                warmupGames: firestoreWarmupGames,
                updatedAt: this.firestore.fieldValue.serverTimestamp(),
            });
            return updatedGame;
        });
    }
    async cancelWarmupGame(lectureId, gameId) {
        const lectureRef = this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId);
        return this.firestore.runTransaction(async (transaction) => {
            const lectureDoc = await transaction.get(lectureRef);
            if (!lectureDoc.exists) {
                throw new Error(`Lecture ${lectureId} not found`);
            }
            const lecture = this.toLecture(lectureDoc);
            lecture.cancelWarmupGame(gameId);
            const cancelledGame = lecture.warmupGames.find((g) => g.id === gameId);
            if (!cancelledGame) {
                throw new Error(`Warm-up game ${gameId} not found`);
            }
            // Convert dates to Firestore timestamps for storage
            const firestoreWarmupGames = lecture.warmupGames.map((g) => convertDatesToFirestore(this.firestore, g));
            transaction.update(lectureRef, {
                warmupGames: firestoreWarmupGames,
                updatedAt: this.firestore.fieldValue.serverTimestamp(),
            });
            return cancelledGame;
        });
    }
    async getWarmupGames(lectureId) {
        const lecture = await this.getLecture(lectureId);
        return lecture.warmupGames;
    }
    async getActiveWarmupGames(lectureId) {
        const lecture = await this.getLecture(lectureId);
        return lecture.getActiveWarmupGames();
    }
    async syncFromSAC(teacherId, sacLectures, windowStart, windowEnd) {
        const lecturesRef = this.firestore.collection(LectureServiceFirestore.LECTURES_COLLECTION);
        // Only fetch this teacher's lectures inside the sync window — previously
        // this read the ENTIRE collection (every coach, all history), which was
        // hundreds of MB and the main cause of the first-load freeze.
        // Requires the composite index (teacherId ASC, lectureDate ASC), managed
        // in the Firebase console.
        const existingSnapshot = await lecturesRef
            .where("teacherId", "==", teacherId)
            .where("lectureDate", ">=", this.firestore.timestamp.fromDate(windowStart))
            .where("lectureDate", "<=", this.firestore.timestamp.fromDate(windowEnd))
            .get();
        const existingLectures = existingSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
        const operations = [];
        // Soft delete lectures in the window that SAC no longer lists.
        // Preserves app-managed data (topics, rolls, warmup games) in Firestore.
        //
        // `status` is deliberately left alone. A lecture missing from a SAC
        // response is not a cancellation — SAC has no cancellation signal — so
        // overwriting the status would destroy the record of a lecture that was
        // actually Started or Completed, with no way to recover it if SAC lists
        // the lecture again.
        const toBeDeleted = existingLectures.filter((l) => !l.deletedInSAC && !sacLectures.find((nl) => nl.cdid === l.id));
        toBeDeleted.forEach((l) => {
            operations.push({
                id: l.id,
                data: {
                    deletedInSAC: true,
                    updatedAt: new Date(),
                },
            });
        });
        sacLectures.forEach((l) => {
            const startTime = new Date(Number(l.startTime) * 1000);
            const endTime = new Date(Number(l.endTime) * 1000);
            const lectureDate = new Date(Number(l.date) * 1000);
            const lectureEndDate = new Date(Number(l.date) * 1000);
            lectureDate.setHours(startTime.getHours());
            lectureDate.setMinutes(startTime.getMinutes());
            lectureDate.setSeconds(startTime.getSeconds());
            lectureEndDate.setHours(endTime.getHours());
            lectureEndDate.setMinutes(endTime.getMinutes());
            lectureEndDate.setSeconds(endTime.getSeconds());
            const existingLecture = existingLectures.find((el) => el.id === l.cdid);
            const lectureData = {
                cdid: l.cdid,
                classroomId: l.classId,
                year: l.year,
                term: l.term,
                day: l.day,
                teacherId: l.coachId,
                firstName: l.firstName,
                lastName: l.lastName,
                lectureDate: this.firestore.timestamp.fromDate(lectureDate),
                lectureEndDate: this.firestore.timestamp.fromDate(lectureEndDate),
                weekNumber: getWeek(lectureDate),
                topicsCovered: l.topicsCovered,
                updatedAt: new Date(),
                deletedInSAC: false,
            };
            // Only brand new lectures get a status. Restoring a soft-deleted
            // lecture is just clearing `deletedInSAC` (set above) — its status was
            // never touched by the delete, so there is nothing to repair.
            if (!existingLecture) {
                lectureData.createdAt = new Date();
                lectureData.status = LectureStatus.Created;
            }
            operations.push({
                id: l.cdid,
                data: lectureData,
            });
        });
        // Firestore batches are limited to 500 operations; commit in chunks so
        // coaches with many lectures don't hit the limit and fail the whole sync.
        const BATCH_LIMIT = 400;
        for (let i = 0; i < operations.length; i += BATCH_LIMIT) {
            const batch = this.firestore.batch();
            for (const op of operations.slice(i, i + BATCH_LIMIT)) {
                batch.set(lecturesRef.doc(op.id), op.data, { merge: true });
            }
            await batch.commit();
        }
        return { deleted: toBeDeleted.length, synced: sacLectures.length };
    }
    async saveLecture(lecture) {
        // Convert dates to Firestore timestamps for storage
        const firestoreWarmupGames = lecture.warmupGames.map((g) => convertDatesToFirestore(this.firestore, g));
        const firestoreActivities = lecture.activities.map((a) => convertDatesToFirestore(this.firestore, a));
        // Only update fields that change during a lecture
        const updateData = {
            status: lecture.status || LectureStatus.Created,
            students: lecture.students || [],
            submittedRoll: lecture.submittedRoll || null,
            autosavedRoll: lecture.autosavedRoll || null,
            leaderboard: lecture.leaderboard || null,
            schoolName: lecture.schoolName || null,
            warmupGames: firestoreWarmupGames,
            activities: firestoreActivities,
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        };
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lecture.id)
            .update(replaceUndefinedWithNull(updateData));
        return lecture;
    }
}
